import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Globe, User, Clock, Calendar, CalendarDays, UserPlus, Trash2, Plus, Pencil, Shield, ListChecks, Gauge, RefreshCw, ChevronDown, Lock, Users, MapPin, Utensils } from 'lucide-react';
import PreferencesTab from './PreferencesTab';
import MembersTab from './MembersTab';
import RegionalAuthorityTab from './RegionalAuthorityTab';
import SystemAuthorityTab from './SystemAuthorityTab';
import PollSettingsTab from './PollSettingsTab';
import CalendarOptionsTab from './CalendarOptionsTab'; // (9 Oct 2026, decisions 52, 59, 60)

// (15 Sep 2026) Mirrors userRoutes.js's normalizeNameForComparison (trim +
// collapse) so the frontend and backend never disagree about what a name
// actually is. Reproduced live: the PUT previously sent firstName.trim()
// (ends only) while the backend collapsed internal whitespace runs too —
// so the stored value differed from what stayed in the input's own local
// state, and nothing ever wrote the corrected value back. Applied on
// blur (below) rather than on every keystroke, so it corrects the field
// as soon as the person leaves it — before Save is even clicked, not
// only as an after-the-fact rewrite once the round trip completes.
const normalizeWhitespace = (value) => (value || '').trim().replace(/\s+/g, ' ');

// (22 Sep 2026) disabled added to support the mandatory-profile-completion
// gate: those tabs stay VISIBLE (per Neil's decision — not hidden the way
// restrictToProfileTabs/restrictToDeputyTabs hide tabs elsewhere in this
// file) but greyed out, non-clickable, with a lock icon and a tooltip
// explaining why, until Profile is complete.
// (3 Oct 2026) Beside a tab's name while it holds unsaved changes.
const UnsavedDot = ({ show }) => (show ? (
  <span className="ml-auto flex items-center gap-1 text-xs font-normal text-amber-600" aria-label="Unsaved changes">
    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
  </span>
) : null);

const TabButton = ({ active, onClick, disabled, children }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    title={disabled ? 'Complete your profile first' : undefined}
    className={`w-full flex items-center gap-3 px-3 py-3 text-left text-sm transition rounded-lg ${
      disabled
        ? 'text-gray-400 cursor-not-allowed opacity-60'
        : active
          ? 'bg-blue-50 text-blue-600 font-semibold'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
    {children}
    {disabled && <Lock className="w-3.5 h-3.5 ml-auto flex-shrink-0" />}
  </button>
);

// ─── Limits Tab ───────────────────────────────────────────────────────────────
// (12 Sep 2026) Renamed from SubscriptionTab, and stripped of everything built
// around a subscription that was never actually sold: no payment gateway
// exists anywhere in this codebase, and the old "Paid plans are coming soon"
// copy confirms a real purchase flow was never built, only ever aspirational.
// tier_expiry_date/"Active"/"Expired" had zero backend enforcement (display
// only), so that whole header card is gone too — showing "Active" implied
// something was being tracked and enforced that never was. What's left is the
// one thing that's genuinely real and still enforced: the plan-limits numbers
// themselves (calendar count — cap-aware via role_assignments.cap through the
// user_subscription_summary view — and events-per-calendar, currently NOT
// cap-aware). See /areas/calendar-hub.md for the fuller audit.
//
// (19 Sep 2026) The "Events per calendar" row is REMOVED. It read the tier's
// max_events_per_calendar, which is null for the only live tier, so it
// displayed "Unlimited events" - false: event limits now live on each
// calendar and are enforced server-side (rolling cap by event date, plus an
// advance-scheduling horizon; see calendars.max_events_per_period etc.).
// Showing the real per-calendar values needs an API change (future: with
// the SA-only limits screen).
//
// (23 Sep 2026) No API change was needed after all: GET /api/calendars
// already selects * from calendars, so currentCalendar carries
// max_events_per_period / event_period_days / max_advance_days /
// max_members. Shown here READ-ONLY in a "This calendar's limits" card, so
// a GA can see the limits that calendarRoutes.js enforces before hitting
// the "Event Limit Reached" dialog. Editing stays SA-only (these columns
// are deliberately excluded from the GA's PATCH route). A value that is
// null/undefined is simply not shown, rather than guessed at.
const CalendarLimitsCard = ({ currentCalendar }) => {
  if (!currentCalendar) return null;
  const {
    max_events_per_period,
    event_period_days,
    max_advance_days,
    max_members,
  } = currentCalendar;

  const has = (v) => v !== null && v !== undefined;
  const hasEventCap = has(max_events_per_period) && has(event_period_days);
  if (!hasEventCap && !has(max_advance_days) && !has(max_members)) return null;

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Icon className="w-4 h-4 text-gray-400 shrink-0" />
        {label}
      </div>
      <span className="text-sm font-medium text-gray-900 text-right">{value}</span>
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 p-5 mb-4">
      <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">This calendar's limits</h5>
      {currentCalendar.name && (
        <p className="text-xs text-gray-500 mb-4">{currentCalendar.name}</p>
      )}
      <div className="space-y-3">
        {hasEventCap && (
          <Row
            icon={CalendarDays}
            label="Events"
            value={`At most ${max_events_per_period} in any ${event_period_days}-day period`}
          />
        )}
        {has(max_advance_days) && (
          <Row
            icon={Clock}
            label="Scheduling ahead"
            value={`Up to ${max_advance_days} days`}
          />
        )}
        {has(max_members) && (
          <Row
            icon={Users}
            label="Members"
            value={`Up to ${max_members}`}
          />
        )}
      </div>
      <p className="text-xs text-gray-500 mt-4">
        These limits are set by Calendar Hub. Please contact us if you need a higher limit.
      </p>
    </div>
  );
};

const LimitsTab = ({ userTier, currentCalendar }) => {
  if (!userTier || !userTier.tier_name) {
    return (
      <div>
        <CalendarLimitsCard currentCalendar={currentCalendar} />
        <p className="text-gray-500">No plan information found.</p>
      </div>
    );
  }

  const {
    tier_name,
    max_calendars,
    min_event_spacing_days,
    term_days,
  } = userTier;

  const isFree = tier_name === 'free_weekly' || tier_name === 'free_monthly';
  const isPaid = !isFree;

  // Limit display helper
  const limitLabel = (val, unit) =>
    val === null || val === undefined ? `Unlimited ${unit}` : `${val} ${unit}`;

  return (
    <div>
      <CalendarLimitsCard currentCalendar={currentCalendar} />

      {/* Plan limits */}
      <div className="rounded-xl border border-gray-200 p-5">
        <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Plan limits</h5>
        <div className="space-y-3">

          {/* Calendars */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4 text-gray-400" />
              Calendars
            </div>
            <span className="text-sm font-medium text-gray-900">
              {limitLabel(max_calendars, max_calendars === 1 ? 'calendar' : 'calendars')}
            </span>
          </div>

          {/* Spacing (free tiers only) */}
          {isFree && min_event_spacing_days && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-gray-400" />
                Minimum days between events
              </div>
              <span className="text-sm font-medium text-gray-900">{min_event_spacing_days} days</span>
            </div>
          )}

          {/* Term (paid tiers) */}
          {isPaid && term_days && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-gray-400" />
                Subscription term
              </div>
              <span className="text-sm font-medium text-gray-900">{term_days} days</span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─── Calendar Tab ─────────────────────────────────────────────────────────────

const CalendarTab = ({ currentCalendar, apiBaseUrl, authToken, onStatusMessage }) => {
  const [standIns, setStandIns] = useState([]);
  const [newStandInEmail, setNewStandInEmail] = useState('');
  const [isLoadingStandIns, setIsLoadingStandIns] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  // (Sep 2026, Amendment 1) While authority is handed off, GET /deputies
  // correctly 403s for the non-active owner (per Amendment 1 — confirmed
  // by reading deputyRoutes.js, no change needed there). Previously this
  // component didn't check res.ok on that fetch, so a 403 silently left
  // standIns as [], rendering "No stand-ins assigned" even when one
  // genuinely exists — misleading. Now tracked explicitly.
  const [standInsAccessDenied, setStandInsAccessDenied] = useState(false);

  // (Sep 2026, Amendment 1) Active-holder status + who holds it, if not
  // you — via the relaxed-gate authority-status endpoint (works for the
  // base owner even while not currently active, unlike ga-standins).
  const [isActiveHolder, setIsActiveHolder] = useState(null); // null = still checking
  const [activeHolderName, setActiveHolderName] = useState(null);
  const [handoffCandidates, setHandoffCandidates] = useState([]);
  const [handoffTarget, setHandoffTarget] = useState('');
  const [isHandingOff, setIsHandingOff] = useState(false);
  const [isReclaiming, setIsReclaiming] = useState(false);

  useEffect(() => {
    if (!currentCalendar?.id) return;
    fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/authority-status`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setIsActiveHolder(data.isActiveHolder);
        setActiveHolderName(data.activeHolderName);
        if (data.isActiveHolder) {
          // Only fetch hand-off candidates when there's actually a
          // handoff to potentially perform.
          fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/ga-standins`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          })
            .then((r) => (r.ok ? r.json() : []))
            .then((list) => setHandoffCandidates(list))
            .catch(() => setHandoffCandidates([]));
        }
      })
      .catch(() => {});
  }, [currentCalendar?.id, apiBaseUrl, authToken]);

  const handleHandOff = async () => {
    if (!handoffTarget) {
      onStatusMessage('Please select a stand-in', 'error');
      return;
    }
    setIsHandingOff(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/hand-off-authority`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ standin_user_id: handoffTarget })
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Authority handed off', 'success');
        window.location.reload();
      } else {
        onStatusMessage(data.error || 'Failed to hand off authority', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to hand off authority', 'error');
    } finally {
      setIsHandingOff(false);
    }
  };

  const handleReclaim = async () => {
    setIsReclaiming(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/reclaim-authority`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (res.ok) {
        onStatusMessage(data.message || 'Authority reclaimed', 'success');
        window.location.reload();
      } else {
        onStatusMessage(data.error || 'Failed to reclaim authority', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to reclaim authority', 'error');
    } finally {
      setIsReclaiming(false);
    }
  };

  useEffect(() => {
    if (!currentCalendar?.id) return;
    const load = async () => {
      setIsLoadingStandIns(true);
      setStandInsAccessDenied(false);
      try {
        const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/deputies`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setStandIns(data);
        } else {
          // deputyRoutes.js returns 404 when resolveCalendarAccess comes
          // back null entirely (the common case for a non-active owner
          // post-Amendment-1), not just 403 — treat any non-ok response
          // as access-denied for display purposes, not only 403.
          setStandInsAccessDenied(true);
        }
      } catch (err) {
        console.error('Failed to load stand-ins:', err);
      } finally {
        setIsLoadingStandIns(false);
      }
    };
    load();
  }, [currentCalendar?.id, apiBaseUrl, authToken]);

  const handleAddStandIn = async () => {
    if (!newStandInEmail.trim()) return;
    setIsAdding(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/deputies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newStandInEmail.trim().toLowerCase() })
      });
      const data = await res.json();
      if (res.ok) {
        setStandIns(prev => [...prev, data]);
        setNewStandInEmail('');
        onStatusMessage('Stand-in added successfully', 'success');
      } else {
        onStatusMessage(data.error || 'Failed to add stand-in', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to add stand-in', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveStandIn = async (email) => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/deputies/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        setStandIns(prev => prev.filter(d => d.deputy_email !== email));
        onStatusMessage('Stand-in removed', 'success');
      } else {
        onStatusMessage('Failed to remove stand-in', 'error');
      }
    } catch (err) {
      onStatusMessage('Failed to remove stand-in', 'error');
    }
  };

  if (!currentCalendar) {
    return <p className="text-gray-500 text-sm">No calendar selected.</p>;
  }

  const atLimit = standIns.length >= 2;

  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">Calendar</h3>
      <p className="text-sm text-gray-500 mb-6">{currentCalendar.name}</p>

      {/* (Sep 2026, Amendment 1) Active-holder status + hand-off/reclaim.
          Add/Remove Stand-in below (deputyRoutes.js) already correctly
          respects this — it gates on access.isOwner from
          resolveCalendarAccess, which post-Amendment-1 only comes back
          true for the base owner AND currently active holder. No
          separate change was needed there; confirmed by reading the
          actual file rather than assuming. */}
      <div className="rounded-xl border border-gray-200 p-5 mb-4">
        <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Authority</h5>
        {isActiveHolder === null ? (
          <p className="text-sm text-gray-400">Checking status…</p>
        ) : isActiveHolder ? (
          <>
            <p className="text-sm text-gray-600 mb-3">You currently hold active authority for this calendar.</p>
            {handoffCandidates.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No stand-ins appointed yet.</p>
            ) : (
              <>
                <select
                  value={handoffTarget}
                  onChange={(e) => setHandoffTarget(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
                >
                  <option value="">Hand off to…</option>
                  {handoffCandidates.map((s) => (
                    <option key={s.roleAssignmentId} value={s.userId} disabled={!s.isCurrentlyEligible}>
                      {(s.preferredName || s.email)}{!s.isCurrentlyEligible ? ' (window not active)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleHandOff}
                  disabled={isHandingOff}
                  className="w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
                >
                  {isHandingOff ? 'Handing off…' : 'Hand Off Authority'}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Authority for this calendar is currently held by {activeHolderName || 'someone else'} — reclaim it to make changes.
            </p>
            <button
              onClick={handleReclaim}
              disabled={isReclaiming}
              className="w-full px-3 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
            >
              {isReclaiming ? 'Reclaiming…' : 'Reclaim Authority'}
            </button>
          </>
        )}
      </div>

      {/* Stand-ins section */}
      <div className="rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-1">
          <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Stand-ins</h5>
          {!standInsAccessDenied && <span className="text-xs text-gray-400">{standIns.length} / 2</span>}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Stand-ins can add, edit, and delete events on this calendar. They must have a Calendar Hub account.
        </p>

        {isLoadingStandIns ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : standInsAccessDenied ? (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            You need to reclaim authority for this calendar before you can view or manage its stand-ins.
          </p>
        ) : (
          <>
            {/* Current stand-ins */}
            {standIns.length === 0 ? (
              <p className="text-sm text-gray-400 italic mb-4">No stand-ins assigned.</p>
            ) : (
              <ul className="space-y-2 mb-4">
                {standIns.map(d => (
                  <li key={d.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-800">{d.deputy_email}</span>
                    <button
                      onClick={() => handleRemoveStandIn(d.deputy_email)}
                      className="text-red-500 hover:text-red-700 transition ml-2"
                      title="Remove stand-in"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add stand-in */}
            {!atLimit ? (
              <div className="flex flex-col gap-2">
                <input
                  type="email"
                  autoComplete="off"
                  value={newStandInEmail}
                  onChange={e => setNewStandInEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddStandIn()}
                  placeholder="standin@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddStandIn}
                  disabled={isAdding || !newStandInEmail.trim()}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserPlus className="w-4 h-4" />
                  {isAdding ? 'Adding…' : 'Add Stand-in'}
                </button>
              </div>
            ) : (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                Maximum of 2 stand-ins reached. Remove one to add another.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Dietary Flags Section (Profile tab) ───────────────────────────────────────
// Self-contained, same pattern as CalendarTab's Stand-ins section: loads and
// mutates via its own dietary-flags endpoints, independent of the Save/Cancel
// flow used for the rest of the Profile tab (each flag is its own row, added
// or removed immediately rather than batched into the /preferences PUT).

const FLAG_LABELS = {
  nut: { icon: '🥜', label: 'Nut' },
  gluten: { icon: '🌾', label: 'Gluten' },
  dairy: { icon: '🥛', label: 'Dairy' },
  egg: { icon: '🥚', label: 'Egg' },
  shellfish: { icon: '🦐', label: 'Shellfish' },
  garlic: { icon: '🧄', label: 'Garlic' },
  tomato: { icon: '🍅', label: 'Tomato' },
  vegan: { icon: '🌱', label: 'Vegan' },
  vegetarian: { icon: '🥕', label: 'Vegetarian' },
  // (29 Oct 2026, decision 156) Diets.
  dairy_free: { icon: '🥛', label: 'Dairy-free' },
  gluten_free: { icon: '🌾', label: 'Gluten-free' },
  pescatarian: { icon: '🐟', label: 'Pescatarian' },
  keto: { icon: '🥑', label: 'Keto' },
  other: { icon: '📝', label: 'Other' },
};
// (29 Oct 2026, decision 156) Three categories, in this order: Allergies, Diets, Aversions.
// The category is chosen first; each offers only its own items. Allergy and Aversion keep
// every item that isn't a diet; Diet offers Dairy-free, Gluten-free, Vegetarian, Vegan,
// Pescatarian, Keto and Other. Kept the same in dietaryFlagsRoutes.js (the server checks it).
const DIETARY_CATEGORIES = [
  { value: 'allergy', label: 'Allergy', pill: 'bg-rose-100 text-rose-800' },
  { value: 'diet', label: 'Diet', pill: 'bg-sky-100 text-sky-800' },
  { value: 'aversion', label: 'Aversion', pill: 'bg-green-100 text-green-800' },
];
const NON_DIET_ITEMS = ['nut', 'gluten', 'dairy', 'egg', 'shellfish', 'garlic', 'tomato', 'other'];
const ITEMS_BY_CATEGORY = {
  allergy: NON_DIET_ITEMS,
  diet: ['dairy_free', 'gluten_free', 'vegetarian', 'vegan', 'pescatarian', 'keto', 'other'],
  aversion: NON_DIET_ITEMS,
};
const categoryOf = (value) => DIETARY_CATEGORIES.find((c) => c.value === value) || DIETARY_CATEGORIES[2];
// The categories an existing entry can be moved between (an Other entry: all three).
const categoriesFor = (flagType) => DIETARY_CATEGORIES.filter((c) => ITEMS_BY_CATEGORY[c.value].includes(flagType));


const DietaryFlagsSection = ({
  apiBaseUrl, authToken, onStatusMessage, setHasChanges,
  flags, setFlags, pendingSeverityChanges, setPendingSeverityChanges,
  pendingAdds, setPendingAdds, pendingRemoveIds, setPendingRemoveIds,
  // (23 Oct 2026, 8.44, decision 125) appliesToChoices: [{ value, label }] for a primary
  // member with a secondary contact on this calendar, else null (no drop-down).
  appliesToChoices, pendingAppliesToChanges, setPendingAppliesToChanges,
}) => {
  const [isLoadingFlags, setIsLoadingFlags] = useState(true);
  const [newFlagType, setNewFlagType] = useState('');
  const [newSeverity, setNewSeverity] = useState('');
  const [newOtherText, setNewOtherText] = useState('');
  const [newAppliesTo, setNewAppliesTo] = useState('both');
  const [editingFlagId, setEditingFlagId] = useState(null);
  const [confirmingRemoveFlag, setConfirmingRemoveFlag] = useState(null);
  // (Sep 2026) flags, pendingSeverityChanges, pendingAdds and
  // pendingRemoveIds are all owned by the parent SettingsModal — every
  // dietary-flag mutation (add, remove, edit severity) is now staged and
  // only committed when Save Changes is clicked, for full consistency
  // with the rest of the Profile tab. Previously Add/Remove were
  // immediate by original design while only the edit path was batched,
  // which read as inconsistent once the edit path changed. Lifting all
  // four also means staged-but-unsaved changes survive switching to
  // another tab and back (this component fully unmounts/remounts on tab
  // switch), and Save Changes can commit them even if this tab isn't the
  // one currently showing.

  useEffect(() => {
    // Skip refetching if the parent already has flags — this component
    // remounts on every tab switch, but flags now live in the parent, so
    // a remount shouldn't silently reload and lose nothing (pending
    // changes are safe either way since they're also in the parent), but
    // there's no need to hit the network again either.
    if (flags.length > 0) {
      setIsLoadingFlags(false);
      return;
    }
    const load = async () => {
      setIsLoadingFlags(true);
      try {
        const res = await fetch(`${apiBaseUrl}/api/users/dietary-flags`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          setFlags(await res.json());
        }
      } catch (err) {
        console.error('Failed to load dietary flags:', err);
      } finally {
        setIsLoadingFlags(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authToken]);

  // The unique index allows only one non-'other' row per flag_type per
  // member — hide types already recorded (including staged adds, and
  // excluding staged removals) so a second attempt can't 409 once saved.
  const usedTypes = flags
    .filter((f) => !pendingRemoveIds.includes(f.id))
    .map((f) => f.flag_type)
    .concat(pendingAdds.map((a) => a.flag_type));
  // (29 Oct 2026, decision 156) The items of the chosen category, less those already recorded.
  const availableTypes = (ITEMS_BY_CATEGORY[newSeverity] || []).filter(
    (t) => t === 'other' || !usedTypes.includes(t)
  );

  // Merged list for rendering: real flags (minus staged removals, with
  // any staged severity override applied) plus staged adds, which carry
  // a synthetic id so they can be edited/un-added before Save Changes
  // the same way a real flag can be edited/removed.
  const displayFlags = [
    ...flags
      .filter((f) => !pendingRemoveIds.includes(f.id))
      .map((f) => ({
        ...f,
        severity: pendingSeverityChanges[f.id] || f.severity,
        applies_to: pendingAppliesToChanges[f.id] || f.applies_to || 'both',
        __pendingSeverity: Boolean(pendingSeverityChanges[f.id]) || Boolean(pendingAppliesToChanges[f.id]),
        __pendingAdd: false,
      })),
    ...pendingAdds.map((a) => ({ ...a, __pendingAdd: true, __pendingSeverity: false })),
  ];

  const handleAddFlag = () => {
    if (!newSeverity || !newFlagType) {
      onStatusMessage(!newSeverity ? 'Please choose Allergy, Diet or Aversion first' : 'Please choose an item', 'error');
      return;
    }
    if (newFlagType === 'other' && !newOtherText.trim()) {
      onStatusMessage('Please describe the "Other" entry', 'error');
      return;
    }
    const tempId = `pending-add-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setPendingAdds((prev) => [...prev, {
      id: tempId,
      flag_type: newFlagType,
      severity: newSeverity,
      other_text: newFlagType === 'other' ? newOtherText.trim() : null,
      applies_to: appliesToChoices ? newAppliesTo : 'both',
    }]);
    setNewFlagType('');
    setNewSeverity('');
    setNewOtherText('');
    setNewAppliesTo('both');
    setHasChanges(true);
  };

  const handleStageSeverity = (id, severity, isPendingAdd) => {
    if (isPendingAdd) {
      setPendingAdds((prev) => prev.map((a) => (a.id === id ? { ...a, severity } : a)));
    } else {
      setPendingSeverityChanges((prev) => ({ ...prev, [id]: severity }));
    }
    setEditingFlagId(null);
    setHasChanges(true);
  };

  // (23 Oct 2026, 8.44, decision 125) Staged like a severity change, saved by Save Changes.
  const handleStageAppliesTo = (id, value, isPendingAdd) => {
    if (isPendingAdd) {
      setPendingAdds((prev) => prev.map((a) => (a.id === id ? { ...a, applies_to: value } : a)));
    } else {
      setPendingAppliesToChanges((prev) => ({ ...prev, [id]: value }));
    }
    setHasChanges(true);
  };
  // Shown in place of the drop-down where there is none (no secondary contact on this
  // calendar): an entry already marked for one of the two.
  const appliesToNote = (value) => (value === 'primary' ? 'For you only'
    : value === 'secondary' ? 'For your secondary contact' : null);

  // (8 Sep 2026) Now gated behind a confirm modal — this used to fire
  // immediately on trash-icon click with no confirmation at all, unlike
  // the GA-proxy dialog and Members tab, which just got styled confirms.
  // The removal itself is still only STAGED here (committed on Save
  // Changes, with the whole tab's Cancel as the larger safety net) — the
  // confirm now guards the staging action, matching the other two flows.
  const handleRemoveFlagConfirmed = (id, isPendingAdd) => {
    if (isPendingAdd) {
      // Never existed server-side — just drop the staged add entirely.
      setPendingAdds((prev) => prev.filter((a) => a.id !== id));
    } else {
      setPendingRemoveIds((prev) => [...prev, id]);
      // A flag about to be removed has nothing left to patch.
      setPendingSeverityChanges((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setPendingAppliesToChanges((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    setHasChanges(true);
    setConfirmingRemoveFlag(null);
  };

  return (
    <div>
      {/* (3 Oct 2026) Now a tab of its own (was the foot of Profile). */}
      <h3 className="text-lg font-semibold mb-1">Allergies, Diets &amp; Aversions</h3>
      <p className="text-sm text-gray-500 mb-4">
        Shown on the poll page, in both phases, to members of events you're attending — a reminder, not a guarantee.
      </p>
      {/* (23 Oct 2026, 8.44, decision 125) */}
      {appliesToChoices && (
        <p className="text-sm text-gray-500 -mt-2 mb-4">
          Use "Applies to" to say whether each one is for {appliesToChoices[1].label}, {appliesToChoices[2].label}, or both of you.
        </p>
      )}

      {isLoadingFlags ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          {displayFlags.length === 0 ? (
            <p className="text-sm text-gray-400 italic mb-3">None recorded yet.</p>
          ) : (
            <ul className="space-y-2 mb-4">
              {displayFlags.map((f) => {
                const isPendingSeverity = f.__pendingSeverity;
                const isPendingAdd = f.__pendingAdd;
                return (
                <li key={f.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-sm text-gray-800 flex items-center gap-2 flex-wrap">
                    <span>{FLAG_LABELS[f.flag_type]?.icon || '📝'}</span>
                    {f.flag_type === 'other' ? f.other_text : FLAG_LABELS[f.flag_type]?.label}
                    {editingFlagId === f.id ? (
                      <select
                        autoFocus
                        value={f.severity}
                        onChange={(e) => handleStageSeverity(f.id, e.target.value, isPendingAdd)}
                        onBlur={() => setEditingFlagId(null)}
                        className="text-xs border border-gray-300 rounded-full px-2 py-0.5"
                      >
                        {categoriesFor(f.flag_type).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    ) : (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${categoryOf(f.severity).pill}`}
                        title={(isPendingSeverity || isPendingAdd) ? 'Not saved yet — click Save Changes' : undefined}
                      >
                        {categoryOf(f.severity).label}
                        {(isPendingSeverity || isPendingAdd) && ' •'}
                      </span>
                    )}
                    {/* (23 Oct 2026, 8.44, decision 125) Whom it is for. */}
                    {appliesToChoices ? (
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        Applies to
                        <select
                          value={f.applies_to || 'both'}
                          onChange={(e) => handleStageAppliesTo(f.id, e.target.value, isPendingAdd)}
                          className="text-xs border border-gray-300 rounded-full px-2 py-0.5"
                        >
                          {appliesToChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </label>
                    ) : appliesToNote(f.applies_to) && (
                      <span className="text-xs text-gray-500">{appliesToNote(f.applies_to)}</span>
                    )}
                    {/* (8 Sep 2026) Proxy-entry provenance — a member should
                        always be able to see when a GA entered this on
                        their behalf, not just have the GA see it. */}
                    {f.enteredByName && (
                      <span className="text-xs italic text-gray-400">Added by {f.enteredByName}</span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 ml-2">
                    {/* (29 Oct 2026, decision 156) Only where the entry could go in another
                        category (a diet item has one category only). */}
                    {categoriesFor(f.flag_type).length > 1 && (
                      <button
                        onClick={() => setEditingFlagId(f.id)}
                        className="text-gray-400 hover:text-gray-600 transition"
                        title="Change category"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmingRemoveFlag({
                        id: f.id,
                        isPendingAdd,
                        label: f.flag_type === 'other' ? f.other_text : FLAG_LABELS[f.flag_type]?.label,
                      })}
                      className="text-red-500 hover:text-red-700 transition"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                </li>
                );
              })}
            </ul>

          )}

          {/* (29 Oct 2026, decision 156) Captioned "Category" and "Item" (Neil). The category first (Allergy, Diet or Aversion),
              then the item, from that category's own list. */}
          {(
            <div className="flex flex-col gap-2 border border-gray-200 rounded-lg p-3">
              <div className="flex gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-600">Category</span>
                  <select
                    value={newSeverity}
                    onChange={(e) => {
                      const sev = e.target.value;
                      setNewSeverity(sev);
                      if (newFlagType && !(ITEMS_BY_CATEGORY[sev] || []).includes(newFlagType)) setNewFlagType('');
                    }}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="">Please select…</option>
                    {DIETARY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <label className="flex-1 min-w-0 flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-600">Item</span>
                  <select
                    value={newFlagType}
                    onChange={(e) => setNewFlagType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="">{newSeverity ? 'Please select…' : 'Choose the category first'}</option>
                    {availableTypes.map((t) => (
                      <option key={t} value={t}>{FLAG_LABELS[t].icon} {FLAG_LABELS[t].label}</option>
                    ))}
                  </select>
                </label>
              </div>
              {newFlagType === 'other' && (
                <input
                  type="text"
                  value={newOtherText}
                  onChange={(e) => setNewOtherText(e.target.value)}
                  placeholder="Describe (e.g. sesame)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              )}
              {/* (23 Oct 2026, 8.44, decision 125) */}
              {appliesToChoices && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  Applies to
                  <select
                    value={newAppliesTo}
                    onChange={(e) => setNewAppliesTo(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  >
                    {appliesToChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
              )}
              {/* (29 Oct 2026) Never disabled (decision 40): pressing it says what is missing. */}
              <button
                onClick={handleAddFlag}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          )}
        </>
      )}

      {confirmingRemoveFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Remove this flag?</h3>
            <p className="text-gray-600 mb-4">
              Remove <span className="font-medium">{confirmingRemoveFlag.label}</span> from your allergies, diets &amp; aversions?
              {!confirmingRemoveFlag.isPendingAdd && ' This is staged until you click Save Changes.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmingRemoveFlag(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveFlagConfirmed(confirmingRemoveFlag.id, confirmingRemoveFlag.isPendingAdd)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Main Modal ──────────────────────────────────────────────────────────────

// (26 Sep 2026, H3) The member's own poll-email setting for the CURRENT
// calendar (calendar_members.email_notifications). Presentational only: the
// value lives in SettingsModal and is saved by the footer's Save Changes,
// like everything else on the Preferences tab (Neil, 26 Sep — a control that
// saved by itself beside a Save button was confusing).
const EMAIL_CHOICES = [
  // (30 Sep 2026, H15) Captions reworded with Neil.
  { value: 'all', label: 'All', detail: 'Every poll email: poll phase 1 and 2 opening and closing, including reminders.' },
  // (8 Oct 2026, decision 28) Shown under a "Polls" caption; "None" became "No poll emails",
  // as event emails are a separate setting.
  { value: 'attending', label: "Only events I'm attending", detail: 'The first email when a poll opens, then emails only for events where I\'ve answered "Yes" to "Are you coming?".' },
  { value: 'none', label: 'No poll emails', detail: "Having made sure you're in the WhatsApp group, choose this option to opt out of all emails relating to polls." },
];

// (30 Sep 2026, H17) Hosting location — opt-in. Only the host sees or
// changes it; events they host show it in members' calendars and as a
// Directions link in the app, never in emails. Withdrawing deletes everything.
// (2 Oct 2026, S1) Now saved by the modal's one Save Changes button (Neil:
// two save buttons confused Gary). The state lives in SettingsModal, like the
// dietary flags, so switching tabs keeps unsaved edits. Also new: the pin is
// described in words ("Pin placed near Courcôme (16700)"), "Find this
// address" sets the pin from the typed address, the address is required, and
// Save warns when the pin and the address are more than 2 km apart.
const EMPTY_HOSTING = {
  loaded: false, saved: null, consent: false, coords: null, address: '', directions: '',
  dirty: false, pinPlace: null, pinSource: null, pinFromAddress: null, pinPrecision: null,
  findNote: null, // { kind: 'street' | 'area' | 'none' } — the line under the Address box
};
const PIN_ADDRESS_LIMIT_KM = 2;

const distanceKm = (a, b) => {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};
const formatKm = (km) => (km < 10 ? km.toFixed(1) : String(Math.round(km)));

// Look up a typed address. Returns { found, lat, lng, precision, label }
// (precision 'house' | 'street' | 'area'); throws
// if the lookup service did not answer.
const lookupAddress = async (apiBaseUrl, authToken, q) => {
  const res = await fetch(`${apiBaseUrl}/api/users/me/geocode?q=${encodeURIComponent(q)}`, {
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'The address lookup is not answering just now.');
  return data;
};
// The town (and postcode) a pin is in, or null.
const lookupPlace = async (apiBaseUrl, authToken, coords) => {
  try {
    const res = await fetch(`${apiBaseUrl}/api/users/me/reverse-geocode?lat=${coords.lat}&lng=${coords.lng}`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.found ? data.place : null;
  } catch (err) {
    return null;
  }
};

const HostingLocationSection = ({ hosting, onChange, onUseCurrentLocation, onFindAddress, onRemove, locating, finding, removing }) => {
  // (3 Oct 2026) Now a whole tab, so say something rather than show a blank.
  if (!hosting.loaded) return <div className="text-center py-8 text-gray-500">Loading...</div>;
  const { consent, coords, address, directions, pinPlace, pinSource, pinPrecision, findNote } = hosting;
  const hasSaved = !!(hosting.saved && hosting.saved.consent);
  const mapLink = coords ? `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}` : null;
  // (2 Oct 2026, S1) One short line, kept on screen (Neil: the 8-second
  // message held too much to read).
  const pinWarn = pinSource === 'address' && (pinPrecision === 'street' || pinPrecision === 'area');
  const pinText = !coords
    ? null
    : pinWarn
      ? (pinPrecision === 'street' ? 'House number not found: the pin is on the street' : 'Only the village was found: the pin is in its centre')
      : pinPlace
        ? (pinSource === 'address' ? `Pin placed at ${pinPlace}` : `Pin placed near ${pinPlace}`)
        : `Pin placed (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`;

  return (
    <div>
      {/* (3 Oct 2026) Now a tab of its own (was the foot of Profile). */}
      <h3 className="text-lg font-semibold mb-1">Hosting Location</h3>
      <p className="text-sm text-gray-500 mb-4">
        This is optional and only needed if you host meetings. When you host an event, members will see a map of your location and directions to reach it.
      </p>

      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => onChange({ consent: e.target.checked })}
          className="mt-1"
        />
        <span className="text-sm text-gray-800">
          Show my hosting location in the group's calendar. Members of this calendar will see it, with a map, in their
          own calendar app, and you'll see a 'Directions' link you can tap next to the location on the event itself
          when viewing in the app.
        </span>
      </label>

      {consent && (
        <div className="mt-3 space-y-3">
          <div>
            <button
              type="button"
              onClick={onUseCurrentLocation}
              disabled={locating}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:opacity-60"
            >
              {locating ? 'Finding your location…' : 'Use my current location'}
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Tapping this button is the most accurate way to store your location, so use it only when you're at the
              place you'll be hosting. Please also type your full address, as it's shown to members in their
              Apple/Google calendars, and their iPhones will need it to display the map.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            {/* (2 Oct 2026, S1) Full width with a clear (×) button; Find below (Neil). */}
            <div className="relative">
              <input
                type="text"
                value={address}
                maxLength={300}
                onChange={(e) => onChange({ address: e.target.value, findNote: null })}
                placeholder="e.g. 12 Chemin des Vignes, 79000 Niort, France"
                className="w-full border border-gray-300 rounded-lg pl-3 pr-9 py-2 text-sm"
              />
              {address && (
                <button
                  type="button"
                  onClick={() => onChange({ address: '', findNote: null })}
                  aria-label="Clear the address"
                  className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={onFindAddress}
              disabled={finding}
              className="w-full mt-2 border border-blue-600 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-50 transition text-sm font-medium disabled:opacity-60"
            >
              {finding ? 'Finding…' : 'Find this address'}
            </button>
            {findNote && findNote.kind === 'none' && (
              <p className="text-xs text-red-700 mt-1">Address not found. Please check the spelling.</p>
            )}
            {pinText && (
              <p className={`text-xs mt-1 ${pinWarn ? 'text-amber-700' : 'text-gray-700'}`}>
                {pinText} ·{' '}
                <a href={mapLink} target="_blank" rel="noopener noreferrer" className="text-blue-700 font-semibold hover:underline">Check on map</a>
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Directions note (optional)</label>
            <textarea
              value={directions}
              maxLength={500}
              rows={2}
              onChange={(e) => onChange({ directions: e.target.value })}
              placeholder="e.g. Second gate after the barn, blue shutters"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      {hasSaved && (
        <button
          type="button"
          onClick={onRemove}
          disabled={removing}
          className="mt-3 text-xs text-red-600 hover:underline"
        >
          Remove my hosting location
        </button>
      )}
    </div>
  );
};

// (2 Oct 2026, S1) Asked on Save when the pin and the typed address are more
// than PIN_ADDRESS_LIMIT_KM apart (e.g. "Use my current location" pressed in
// Paris for a house in Courcôme).
const PinMismatchDialog = ({ dialog, onChoose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-center justify-center p-4">
    <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-lg">
      <h3 className="text-base font-semibold text-gray-800">Your pin and your address don't match</h3>
      <p className="text-sm text-gray-700 mt-2">
        Your pin is about {formatKm(dialog.km)} km from the address you typed
        {dialog.pinPlace ? ` (pin near ${dialog.pinPlace})` : ''}. Members would be directed to the pin.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => onChoose('address')}
          className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Use the address's position
        </button>
        <button
          type="button"
          onClick={() => onChoose('pin')}
          className="w-full border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
        >
          Keep my pin
        </button>
        <button
          type="button"
          onClick={() => onChoose('cancel')}
          className="w-full text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const EmailNotificationsSetting = ({ calendarName, value, onChange, eventUpdates, onEventUpdatesChange, administratorFirstName, isAdministrator }) => (
  <>
  {/* (30 Sep 2026, H15) WhatsApp line (Neil) — hidden for the administrator. */}
  {!isAdministrator && (
    <div className="mt-6 border-t pt-5">
      <h4 className="text-sm font-semibold text-gray-800">WhatsApp notifications</h4>
      <p className="text-xs text-gray-500 mt-1">
        If you use WhatsApp, make sure you've been added to the group's chat by the administrator{administratorFirstName ? ` (${administratorFirstName})` : ''}.
      </p>
    </div>
  )}
  <div className="mt-6 border-t pt-5">
    <h4 className="text-sm font-semibold text-gray-800">Email notifications for {calendarName}</h4>
    <p className="text-xs text-gray-500 mt-1 mb-3">
      Choose which emails you'll receive for this calendar.
    </p>
    {/* (8 Oct 2026, decision 28) Event emails first, then the poll emails under "Polls".
        A "Schedule emails" box (decision 16) will join this first block. */}
    {/* (30 Sep 2026, H15) Event add/change/cancel emails, for members who can't use WhatsApp. */}
    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
      eventUpdates ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
    }`}>
      <input
        type="checkbox"
        checked={!!eventUpdates}
        onChange={(e) => onEventUpdatesChange(e.target.checked)}
        className="mt-1"
      />
      <span>
        <span className="block text-sm font-medium text-gray-800">Email me when events are added, changed or cancelled</span>
        <span className="block text-xs text-gray-600">For members who do not want to, or cannot, use WhatsApp.</span>
      </span>
    </label>
    <h5 className="text-sm font-semibold text-gray-800 mt-5 mb-2">Polls</h5>
    <div className="space-y-2">
      {EMAIL_CHOICES.map((c) => (
        <label
          key={c.value}
          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
            value === c.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          <input
            type="radio"
            name="email-notifications"
            value={c.value}
            checked={value === c.value}
            onChange={() => onChange(c.value)}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">{c.label}</span>
            <span className="block text-xs text-gray-600">{c.detail}</span>
          </span>
        </label>
      ))}
    </div>
    <p className="text-xs text-gray-400 mt-3">Sign-in links and the email telling you you've been added are always sent.</p>
  </div>
  </>
);

const SettingsModal = ({ isOpen, onClose, userEmail, apiBaseUrl, authToken, onStatusMessage, initialTab, userTier, currentCalendar, firstName: initialFirstName, lastName: initialLastName, onSaveProfile, restrictToProfileTabs = false, restrictToDeputyTabs = false, isSA = false, raRegions = [], authorityCalendars = [], onAnswerPollForMember, onCalendarOptionsSaved, requireProfileCompletion = false, onProfileCompleted, secondaryOnly = false }) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'profile');
  // (21 Sep 2026) "There is more below" cue for the scrolling content area. iPhone hides its
  // scrollbar until you swipe, so a soft fade appears at the bottom edge while more content is
  // hidden below, and disappears once you reach the end. (The scrollbar itself is also made
  // permanently visible: see the settings-scroll style below.)
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const updateScrollHint = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 12);
  };
  const [preferences, setPreferences] = useState({
    currency: 'USD',
    dateFormat: 'DD-MMM-YYYY',
    // (24 Oct 2026, 8.40a, decision 100) Day of the week in front of event dates.
    showWeekday: true,
    timezone: 'Europe/Paris', // (30 Sep 2026, H16) matches the users.preferred_timezone default
    language: 'en',
    // (26 Sep 2026) Pop-up message times, seconds (administrators set these).
    messageSeconds: 4,
    errorMessageSeconds: 8,
  });
  // (26 Sep 2026) The message times as last loaded/saved, so an out-of-range
  // entry can be put back to what's actually in force when Save is refused.
  const savedMessageTimesRef = useRef({ messageSeconds: 4, errorMessageSeconds: 8 });
  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [preferredName, setPreferredName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [defaultPartySize, setDefaultPartySize] = useState(2);
  // (15 Sep 2026) Couple/secondary-contact feature — self-service field on
  // Settings > Profile. Calendar-scoped (lives on calendar_members, not
  // users), so it's loaded/saved against currentCalendar.id via its own
  // GET/PATCH, not folded into the batched preferences PUT below.
  const [secondaryContactEmail, setSecondaryContactEmail] = useState('');
  const [secondaryContactBaseline, setSecondaryContactBaseline] = useState('');
  // (23 Oct 2026, 8.44, decision 125) The partner's First Name (null until they sign in).
  const [secondaryFirstName, setSecondaryFirstName] = useState(null);
  // (21 Oct 2026, decision 120) The box itself, so Save reads what is actually on screen.
  // On an iPhone, Safari's suggestion bar (AutoFill) can fill the box without telling the
  // page, which then kept its own empty copy: Carolyn's two saves on 19 Oct said
  // "Settings saved!" and sent nothing for her partner.
  const secondaryContactInputRef = useRef(null);
  // (15 Sep 2026) Replaces the old isPrimaryMember boolean — 'primary' |
  // 'secondary' | 'none'. Secondary-contact-email visibility now checks
  // memberRole === 'primary' (same meaning as the old flag). The new
  // 'secondary' value additionally drives Preferred Name below: a
  // secondary contact has no sensible value of their own to put there
  // (can't reuse the primary's — blocked by global preferred_name
  // uniqueness, it already belongs to that account — and anything else
  // typed becomes a disconnected identity nothing displays but that
  // still occupies a slot in that same global namespace). primaryName
  // holds the primary's own preferred_name, read-only display only.
  const [memberRole, setMemberRole] = useState('none');
  const [primaryName, setPrimaryName] = useState('');
  // (7 Oct 2026, 8.28) On the first-sign-in screen no calendar is loaded yet, so
  // memberRole stays 'none' even for a secondary contact. landing-context already knows
  // (isSecondaryOnly), so such a person is shown the secondary layout there too: no
  // Preferred Name or Default Party Size to fill in, First and Last Name only.
  const onboardingSecondary = requireProfileCompletion && secondaryOnly && memberRole !== 'primary';
  const showAsSecondary = memberRole === 'secondary' || onboardingSecondary;
  // (8 Oct 2026, decision 29 fix) Settings is opened from seven different screens, and
  // only the first-sign-in one passes secondaryOnly. So the modal asks landing-context
  // itself each time it opens (one small request) and hides Hosting Location for a
  // person who is only ever a secondary contact, wherever Settings was opened from.
  const [secondaryOnlyFetched, setSecondaryOnlyFetched] = useState(false);
  useEffect(() => {
    if (!isOpen || secondaryOnly || !authToken || !apiBaseUrl) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/users/landing-context`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const ctx = await res.json();
        if (!cancelled) setSecondaryOnlyFetched(Boolean(ctx.isSecondaryOnly));
      } catch (_) {
        // Non-fatal: the tab simply stays visible.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, secondaryOnly, authToken, apiBaseUrl]);
  const hideHostingTab = secondaryOnly || secondaryOnlyFetched;
  // (27 Oct 2026, decision 149) A region-scoped Group Admin appointment (the right to
  // create calendars, with its cap) was missing from "Your roles". landing-context
  // doesn't carry the region or the cap, so the modal asks for them itself on opening.
  const [gaRegions, setGaRegions] = useState([]);
  useEffect(() => {
    if (!isOpen || !authToken || !apiBaseUrl) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/authority/my-group-admin-regions`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const rows = await res.json();
        if (!cancelled) setGaRegions(Array.isArray(rows) ? rows : []);
      } catch (_) {
        // Non-fatal: the line is simply not shown.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, authToken, apiBaseUrl]);
  useEffect(() => {
    if (hideHostingTab && activeTab === 'hosting') setActiveTab('profile');
  }, [hideHostingTab, activeTab]);
  // (15 Sep 2026) Same reasoning as primaryName above, per Neil's
  // confirmation — attendance confirmation on the shared row always uses
  // the PRIMARY's own default_party_size (verified in pollsService.js's
  // defaultPartySizeForMember — same calendar_members_user_id_fkey join
  // as preferred_name), so a secondary's own separate value would never
  // actually be used for anything.
  const [primaryDefaultPartySize, setPrimaryDefaultPartySize] = useState(2);
  // (19 Sep 2026) Which text fields the person has typed in (or accepted a
  // browser suggestion for) since the modal opened. The preferences fetch
  // below resolves asynchronously and used to overwrite these fields
  // unconditionally, so anything entered before it landed - an autofill
  // suggestion accepted early, or a brand-new user typing their name on a
  // slow connection (their stored value is blank) - was silently replaced.
  // Touched fields are now skipped by that fetch.
  // Replaces the 15 Sep readOnly-until-200ms-after-open workaround for
  // Firefox form-history suggestions: the dropdown still appeared with it
  // in place (that is how the 16 Sep lock-up was found), so it was not
  // achieving its purpose, and it is the most likely cause of a field
  // becoming uneditable after a suggestion was accepted.
  // autoComplete="off" and the non-matching name attributes stay. Firefox
  // deliberately disregards autocomplete="off" for its own form-history, so
  // a cosmetic suggestion dropdown (in practice only the operator's own
  // repeated test data) is accepted rather than fought.
  const touchedRef = useRef({ firstName: false, lastName: false, preferredName: false, phoneNumber: false, secondaryContactEmail: false });
  const markTouched = (field) => { touchedRef.current[field] = true; };
  
  const [hasChanges, setHasChangesRaw] = useState(false);
  // (3 Oct 2026) Which tabs hold unsaved changes, for the dot beside their
  // names (one Save Changes covers every tab, so a change made on one tab
  // is easy to forget after moving to another). A change belongs to the tab
  // that was open when it was made; a successful save clears them all.
  const [changedTabs, setChangedTabs] = useState([]);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const setHasChanges = useCallback((v) => {
    setHasChangesRaw(v);
    if (v === false) {
      setChangedTabs([]);
    } else {
      const tab = activeTabRef.current;
      setChangedTabs((t) => (t.includes(tab) ? t : [...t, tab]));
    }
  }, []);
  // (2 Oct 2026, S1) Save Changes is never disabled (only while saving).
  // (3 Oct 2026) It reads "Saved ✓" whenever there is nothing unsaved.
  const [isSaving, setIsSaving] = useState(false);
  // (2 Oct 2026, S1) Hosting location, lifted here from its section so the
  // one Save Changes saves it and switching tabs keeps unsaved edits.
  const [hosting, setHosting] = useState(EMPTY_HOSTING);
  const [hostingLocating, setHostingLocating] = useState(false);
  const [hostingFinding, setHostingFinding] = useState(false);
  const [hostingRemoving, setHostingRemoving] = useState(false);
  const [pinDialog, setPinDialog] = useState(null); // { km, pinPlace, resolve }
  // extra: what is known about the pin just saved (from an address lookup),
  // so the line under the Address box stays as it was.
  const applyHosting = (d, extra = null) => {
    const coords = d.lat != null && d.lng != null ? { lat: d.lat, lng: d.lng } : null;
    setHosting({
      ...EMPTY_HOSTING, loaded: true, saved: d, consent: !!d.consent, coords,
      address: d.address || '', directions: d.directions || '',
      ...(coords && extra ? extra : {}),
    });
    if (coords && !(extra && extra.pinPlace)) {
      lookupPlace(apiBaseUrl, authToken, coords).then((place) => {
        if (place) setHosting((h) => (h.coords && h.coords.lat === coords.lat && h.coords.lng === coords.lng ? { ...h, pinPlace: place, pinSource: 'device' } : h));
      });
    }
  };
  useEffect(() => {
    let cancelled = false;
    setHosting(EMPTY_HOSTING);
    // (3 Oct 2026) Unsaved location edits are dropped on reopening, so
    // the Hosting Location tab's unsaved-changes dot goes with them.
    if (isOpen) setChangedTabs((t) => t.filter((x) => x !== 'hosting'));
    if (!isOpen || requireProfileCompletion) return undefined;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/users/me/hosting-location`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        const data = await res.json();
        if (!cancelled && res.ok) applyHosting(data);
      } catch (err) {
        // Section stays hidden; nothing else affected.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, apiBaseUrl, authToken, requireProfileCompletion]);

  const changeHosting = (patch) => {
    setHosting((h) => ({ ...h, ...patch, dirty: true }));
    setHasChanges(true);
  };

  const useCurrentHostingLocation = () => {
    if (!navigator.geolocation) {
      onStatusMessage('This device cannot share its location', 'error');
      return;
    }
    setHostingLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        changeHosting({ coords, pinPlace: null, pinSource: 'device', pinFromAddress: null, pinPrecision: null, findNote: null });
        setHostingLocating(false);
        const place = await lookupPlace(apiBaseUrl, authToken, coords);
        if (place) setHosting((h) => (h.coords === coords ? { ...h, pinPlace: place } : h));
      },
      () => {
        setHostingLocating(false);
        onStatusMessage('Could not get your location — please allow location access and try again', 'error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const findHostingAddress = async () => {
    const q = hosting.address.trim();
    if (!q) {
      onStatusMessage('Type your address first, then use Find this address.', 'error');
      return;
    }
    setHostingFinding(true);
    try {
      const r = await lookupAddress(apiBaseUrl, authToken, q);
      if (!r.found) {
        setHosting((h) => ({ ...h, findNote: { kind: 'none' } }));
        return;
      }
      changeHosting({
        coords: { lat: r.lat, lng: r.lng }, pinPlace: r.label || null, pinSource: 'address',
        pinFromAddress: q, pinPrecision: r.precision || 'house', findNote: null,
      });
    } catch (err) {
      onStatusMessage(err.message, 'error');
    } finally {
      setHostingFinding(false);
    }
  };

  const removeHosting = async () => {
    if (!window.confirm('Remove your hosting location? It will disappear from every event you host.')) return;
    setHostingRemoving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/users/me/hosting-location`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not remove your hosting location');
      applyHosting(data);
      onStatusMessage('Hosting location removed', 'success');
    } catch (err) {
      onStatusMessage(err.message, 'error');
    } finally {
      setHostingRemoving(false);
    }
  };

  // (3 Oct 2026) Opens the Hosting Location tab too, so the pin and address
  // the question is about are on screen behind it.
  const askPinMismatch = (km, pinPlace) => new Promise((resolve) => {
    setActiveTab('hosting');
    setPinDialog({ km, pinPlace, resolve });
  });

  // Works out, before anything is saved, what Save should do with the
  // hosting location: { action: 'none' | 'save' | 'delete' | 'skip' | 'cancel', body, note }.
  // 'skip' = not saved (note says why), the rest of Settings still saves.
  // 'cancel' = the person chose Cancel: nothing at all is saved.
  const planHostingSave = async () => {
    const h = hosting;
    if (!h.loaded || !h.dirty) return { action: 'none' };
    const saved = h.saved || {};
    if (!h.consent) {
      if (!saved.consent) return { action: 'none' };
      if (!window.confirm('You unticked the box, so your hosting location will be removed from every event you host. Continue?')) {
        return { action: 'cancel' };
      }
      return { action: 'delete' };
    }
    const addr = h.address.trim();
    if (!addr) {
      return { action: 'skip', note: 'Hosting location not saved: please enter your address.' };
    }
    const body = (coords) => ({ consent: true, lat: coords.lat, lng: coords.lng, address: addr, directions: h.directions });
    const samePlace = h.coords && saved.lat === h.coords.lat && saved.lng === h.coords.lng && (saved.address || '').trim() === addr;
    // Only the consent box or the directions note changed, or the pin was
    // just set from this very address: nothing to check.
    const keep = { pinPlace: h.pinPlace, pinSource: h.pinSource, pinFromAddress: h.pinFromAddress, pinPrecision: h.pinPrecision };
    if (samePlace || (h.coords && h.pinSource === 'address' && h.pinFromAddress === addr)) {
      return { action: 'save', body: body(h.coords), extra: h.pinPlace ? keep : null };
    }
    const fromLookup = (f) => ({ pinPlace: f.label || null, pinSource: 'address', pinFromAddress: addr, pinPrecision: f.precision || 'house' });
    let found;
    try {
      found = await lookupAddress(apiBaseUrl, authToken, addr);
    } catch (err) {
      if (!h.coords) return { action: 'skip', note: 'Hosting location not saved: the address lookup is not answering. Please try again later.' };
      return { action: 'save', body: body(h.coords), extra: h.pinPlace ? keep : null };
    }
    if (!h.coords) {
      if (!found.found) {
        setHosting((x) => ({ ...x, findNote: { kind: 'none' } }));
        return { action: 'skip', note: 'Hosting location not saved: address not found.' };
      }
      return { action: 'save', body: body({ lat: found.lat, lng: found.lng }), extra: fromLookup(found) };
    }
    if (!found.found) {
      // Can't check: keep the pin as it is.
      return { action: 'save', body: body(h.coords), extra: h.pinPlace ? keep : null };
    }
    const km = distanceKm(h.coords, { lat: found.lat, lng: found.lng });
    if (km <= PIN_ADDRESS_LIMIT_KM) return { action: 'save', body: body(h.coords), extra: h.pinPlace ? keep : null };
    const choice = await askPinMismatch(km, h.pinSource === 'device' ? h.pinPlace : null);
    if (choice === 'cancel') return { action: 'cancel' };
    if (choice === 'address') return { action: 'save', body: body({ lat: found.lat, lng: found.lng }), extra: fromLookup(found) };
    return { action: 'save', body: body(h.coords), extra: h.pinPlace ? keep : null };
  };

  // Carries out the plan. Returns true if it went through (or nothing was needed).
  const runHostingSave = async (plan) => {
    if (plan.action === 'save' || plan.action === 'delete') {
      const res = await fetch(`${apiBaseUrl}/api/users/me/hosting-location`, {
        method: plan.action === 'save' ? 'PUT' : 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: plan.action === 'save' ? JSON.stringify(plan.body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        onStatusMessage(data.error || 'Could not save your hosting location', 'error');
        return false;
      }
      applyHosting(data, plan.extra || null);
    } else if (plan.action === 'none') {
      setHosting((h) => ({ ...h, dirty: false }));
    }
    return true;
  };
  // (26 Sep 2026, H3) This member's email setting for currentCalendar.
  // saved = what the server holds; value = what's chosen on screen.
  // isMember false = not a member of this calendar -> section hidden.
  // (30 Sep 2026, H15) eventUpdates / savedEventUpdates: the "Also email me
  // when events are added, changed or cancelled" box, saved the same way.
  const [emailNotif, setEmailNotif] = useState({ isMember: false, value: null, saved: null, eventUpdates: false, savedEventUpdates: false, administratorFirstName: null, isAdministrator: false, calendarTimezone: null });
  const emailNotifCalendarId = currentCalendar?.id;
  useEffect(() => {
    let cancelled = false;
    setEmailNotif({ isMember: false, value: null, saved: null, eventUpdates: false, savedEventUpdates: false, administratorFirstName: null, isAdministrator: false, calendarTimezone: null });
    if (!isOpen || !emailNotifCalendarId) return undefined;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/calendars/${emailNotifCalendarId}/me/email-notifications`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const v = data.emailNotifications || 'all';
        const ev = !!data.emailEventUpdates;
        if (!cancelled) setEmailNotif({
          isMember: true, value: v, saved: v, eventUpdates: ev, savedEventUpdates: ev,
          administratorFirstName: data.administratorFirstName || null,
          isAdministrator: !!data.isAdministrator,
          calendarTimezone: data.calendarTimezone || null,
        });
      } catch (err) {
        // Leave the section hidden; the rest of Preferences still works.
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, emailNotifCalendarId, apiBaseUrl, authToken]);
  const [dietaryFlags, setDietaryFlags] = useState([]);
  const [pendingSeverityChanges, setPendingSeverityChanges] = useState({});
  // (23 Oct 2026, 8.44, decision 125) Staged "Applies to" changes, { flagId: value }.
  const [pendingAppliesToChanges, setPendingAppliesToChanges] = useState({});
  const [pendingAdds, setPendingAdds] = useState([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    updateScrollHint();
    if (typeof ResizeObserver === 'undefined' || !contentRef.current) return undefined;
    const observer = new ResizeObserver(updateScrollHint);
    observer.observe(contentRef.current);
    if (scrollRef.current) observer.observe(scrollRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoading]);
  // (15 Sep 2026) Poll Settings' Save button moved here, into the shared
  // footer, so it stays visible regardless of how far down that tab's
  // own scroll area the person is — previously self-contained at the
  // bottom of the tab, easy to miss and pushed further down once the
  // reminder-day fields were added 14 Sep. PollSettingsTab still owns
  // all of its own fetch/save logic; this just mirrors its
  // hasChanges/isSaving state up so the footer button can reflect it,
  // and calls back down via the ref to actually trigger the save.
  const pollSettingsRef = useRef(null);
  // (9 Oct 2026, decision 60) Calendar Options saves with the footer button too.
  const calendarOptionsRef = useRef(null);
  const [calendarOptionsHasChanges, setCalendarOptionsHasChanges] = useState(false);
  const [calendarOptionsIsSaving, setCalendarOptionsIsSaving] = useState(false);
  const [pollSettingsHasChanges, setPollSettingsHasChanges] = useState(false);
  const [pollSettingsIsSaving, setPollSettingsIsSaving] = useState(false);

  // Load preferences from API on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/users/preferences`, {
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setPreferences({
            currency: data.preferred_currency || 'USD',
            dateFormat: data.preferred_date_format || 'DD-MMM-YYYY',
            showWeekday: data.show_weekday !== false, // (24 Oct 2026, 8.40a)
            timezone: data.preferred_timezone || 'Europe/Paris',
            language: data.preferred_language || 'en',
            messageSeconds: data.message_seconds || 4,
            errorMessageSeconds: data.error_message_seconds || 8,
          });
          savedMessageTimesRef.current = {
            messageSeconds: data.message_seconds || 4,
            errorMessageSeconds: data.error_message_seconds || 8,
          };
          // (19 Sep 2026) Skip any field already touched - see touchedRef.
          if (!touchedRef.current.firstName) setFirstName(data.first_name || '');
          if (!touchedRef.current.lastName) setLastName(data.last_name || '');
          if (!touchedRef.current.preferredName) setPreferredName(data.preferred_name || '');
          if (!touchedRef.current.phoneNumber) setPhoneNumber(data.phone_number || '');
          setDefaultPartySize(data.default_party_size ?? 2);
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      if (initialTab) setActiveTab(initialTab);
      loadPreferences();
    }
  }, [isOpen, apiBaseUrl, authToken, initialTab]);

  // (15 Sep 2026) Checks role first (via the new /me/identity route),
  // then only fetches the actual secondary-contact-email value when the
  // role is 'primary' — a secondary or unrelated identity has no reason
  // to make that second call at all. A non-200/failure on either call
  // just means this doesn't apply to this identity/calendar combination
  // — not a real error to surface.
  useEffect(() => {
    const loadIdentity = async () => {
      if (!currentCalendar?.id) {
        setMemberRole('none');
        return;
      }
      try {
        const idResponse = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/me/identity`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!idResponse.ok) {
          setMemberRole('none');
          return;
        }
        const idData = await idResponse.json();
        setMemberRole(idData.role);
        setPrimaryName(idData.primaryPreferredName || '');
        setPrimaryDefaultPartySize(idData.primaryDefaultPartySize ?? 2);

        if (idData.role === 'primary') {
          const scResponse = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/me/secondary-contact`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (scResponse.ok) {
            const scData = await scResponse.json();
            // (19 Sep 2026) Skip overwriting the field if the person has already
            // typed in it or accepted a browser suggestion (see touchedRef) - this
            // load takes two round trips, a long window for that to happen. The
            // BASELINE is always updated to the server's value regardless, because
            // handleSave compares field vs baseline to decide whether a change
            // needs sending.
            if (!touchedRef.current.secondaryContactEmail) setSecondaryContactEmail(scData.secondaryContactEmail || '');
            setSecondaryContactBaseline(scData.secondaryContactEmail || '');
            setSecondaryFirstName(scData.secondaryFirstName || null);
          }
        }
      } catch (error) {
        setMemberRole('none');
      }
    };

    if (isOpen) {
      loadIdentity();
    }
  }, [isOpen, apiBaseUrl, authToken, currentCalendar]);

  // (15 Sep 2026) Fix: scrolling with the mouse over the modal's own tab
  // content scrolled the page behind it instead. Root cause — the tab
  // content div IS overflow-y-auto and DOES scroll internally, but on
  // reaching its own top/bottom the wheel event chains straight past the
  // several overflow-hidden wrapper divs above it (they're not
  // scrollable, so browsers skip them when looking for the next scroll
  // target) all the way to document.body, which had no lock of its own.
  // Two-part fix: this body lock stops the page behind from scrolling at
  // all while open; overscroll-contain on the tab content div (below)
  // stops the chaining at the boundary too, belt-and-braces.
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, [isOpen]);

  // (19 Sep 2026) Fresh slate for the touched-field guard each time the modal
  // opens (this component stays mounted and returns null while closed).
  // Declared after the preferences-load effect on purpose: both run in the
  // same commit, before that fetch can resolve, so the reset always wins.
  useEffect(() => {
    if (isOpen) touchedRef.current = { firstName: false, lastName: false, preferredName: false, phoneNumber: false, secondaryContactEmail: false };
  }, [isOpen]);

  // (2 Oct 2026, S1) Never disabled: with nothing changed it says so.
  const handleSave = async () => {
    if (isSaving) return;
    if (!hasChanges && !requireProfileCompletion) {
      onStatusMessage('No changes to save', 'success');
      return;
    }
    setIsSaving(true);
    try {
      await doSave();
    } finally {
      setIsSaving(false);
    }
  };

  const doSave = async () => {
    // (26 Sep 2026) Message times: whole seconds, 2 to 30. An entry outside
    // that goes back to its saved value (Neil), and nothing is saved.
    const badTimes = [
      ['messageSeconds', 'Ordinary messages'],
      ['errorMessageSeconds', 'Error messages'],
    ].filter(([key]) => {
      const n = Number(preferences[key]);
      return !(Number.isInteger(n) && n >= 2 && n <= 30);
    });
    if (badTimes.length > 0) {
      setPreferences((p) => {
        const next = { ...p };
        badTimes.forEach(([key]) => { next[key] = savedMessageTimesRef.current[key]; });
        return next;
      });
      onStatusMessage(`${badTimes[0][1]}: choose a whole number of seconds from 2 to 30.`, 'error');
      return;
    }
    // (22 Sep 2026, Neil's decision) First/Last name mandatory for
    // everyone; Preferred Name mandatory unless this account is
    // secondary-only (memberRole here is per-currentCalendar, but during
    // the onboarding/profile-completion flow there's no currentCalendar
    // yet, so memberRole stays 'none' and Preferred Name is correctly
    // required there too — matches userRoutes.js's authoritative,
    // account-wide isSecondaryOnlyEverywhere check, which is the real
    // gate; this is just an early client-side check so the person doesn't
    // wait on a round trip to find out). Checked first, before any
    // network calls (including the dietary-flag commits below), so a
    // blank required field never partially saves something else first.
    const normalizedFirstNameCheck = normalizeWhitespace(firstName);
    const normalizedLastNameCheck = normalizeWhitespace(lastName);
    const normalizedPreferredNameCheck = normalizeWhitespace(preferredName);
    if (!normalizedFirstNameCheck || !normalizedLastNameCheck) {
      onStatusMessage('First name and last name are required', 'error');
      return;
    }
    if (!normalizedPreferredNameCheck && !showAsSecondary) {
      onStatusMessage('Preferred name is required', 'error');
      return;
    }

    // (2 Oct 2026, S1) Hosting location: decided before anything is
    // written, so Cancel in the pin/address question saves nothing at all.
    const hostingPlan = await planHostingSave();
    // (3 Oct 2026) Say so, so Cancel can't be mistaken for a save.
    if (hostingPlan.action === 'cancel') {
      onStatusMessage('Nothing saved.', 'success');
      return;
    }

    try {
      // Commit any staged dietary-flag changes first — removals, then
      // severity edits, then adds, in that order (removing before adding
      // avoids a false 409 if someone swaps out a flag_type for a new one
      // of the same type in the same sitting). If any step fails, stop
      // here rather than silently saving the rest and losing the error.
      // Runs directly against the lifted state, so it works even if the
      // Profile tab (where the flags UI lives) isn't the tab currently
      // showing when Save is clicked.
      let workingFlags = dietaryFlags;

      if (pendingRemoveIds.length > 0) {
        for (const flagId of pendingRemoveIds) {
          try {
            const delRes = await fetch(`${apiBaseUrl}/api/users/dietary-flags/${flagId}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${authToken}` }
            });
            if (!delRes.ok) {
              const data = await delRes.json().catch(() => ({}));
              onStatusMessage(data.error || 'Failed to save dietary flag changes', 'error');
              return;
            }
          } catch (err) {
            onStatusMessage('Failed to save dietary flag changes', 'error');
            return;
          }
        }
        workingFlags = workingFlags.filter((f) => !pendingRemoveIds.includes(f.id));
      }

      // (23 Oct 2026, 8.44, decision 125) A flag is patched when its severity or its
      // "Applies to" changed; both go in the one PATCH.
      const pendingEntries = [...new Set([
        ...Object.keys(pendingSeverityChanges),
        ...Object.keys(pendingAppliesToChanges),
      ])].map((flagId) => [flagId, pendingSeverityChanges[flagId]]);
      if (pendingEntries.length > 0) {
        for (const [flagId, stagedSeverity] of pendingEntries) {
          const flag = workingFlags.find((f) => f.id === flagId);
          if (!flag) continue; // was also staged for removal — nothing to patch
          const severity = stagedSeverity || flag.severity;
          try {
            const patchRes = await fetch(`${apiBaseUrl}/api/users/dietary-flags/${flagId}`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                severity,
                other_text: flag?.flag_type === 'other' ? flag.other_text : undefined,
                applies_to: pendingAppliesToChanges[flagId] || undefined,
              })
            });
            if (!patchRes.ok) {
              const data = await patchRes.json().catch(() => ({}));
              onStatusMessage(data.error || 'Failed to save dietary flag changes', 'error');
              return;
            }
          } catch (err) {
            onStatusMessage('Failed to save dietary flag changes', 'error');
            return;
          }
        }
        workingFlags = workingFlags.map((f) => (
          // (8 Sep 2026) enteredByName reset to null alongside severity —
          // the server now clears provenance on a self-edit (see the PATCH
          // route above), so the local merge needs to match or the UI
          // would show a stale "Added by" tag until the next full reload.
          (pendingSeverityChanges[f.id] || pendingAppliesToChanges[f.id])
            ? {
              ...f,
              severity: pendingSeverityChanges[f.id] || f.severity,
              applies_to: pendingAppliesToChanges[f.id] || f.applies_to,
              enteredByName: null,
            }
            : f
        ));
      }

      if (pendingAdds.length > 0) {
        for (const add of pendingAdds) {
          try {
            const postRes = await fetch(`${apiBaseUrl}/api/users/dietary-flags`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                flag_type: add.flag_type,
                severity: add.severity,
                other_text: add.flag_type === 'other' ? add.other_text : undefined,
                applies_to: add.applies_to || 'both',
              })
            });
            const data = await postRes.json().catch(() => ({}));
            if (!postRes.ok) {
              onStatusMessage(data.error || 'Failed to save dietary flag changes', 'error');
              return;
            }
            workingFlags = [...workingFlags, data.data];
          } catch (err) {
            onStatusMessage('Failed to save dietary flag changes', 'error');
            return;
          }
        }
      }

      if (pendingRemoveIds.length > 0 || pendingEntries.length > 0 || pendingAdds.length > 0) {
        setDietaryFlags(workingFlags);
        setPendingRemoveIds([]);
        setPendingSeverityChanges({});
        setPendingAppliesToChanges({});
        setPendingAdds([]);
      }

      // (15 Sep 2026) Secondary-contact-email — its own endpoint, own
      // table (calendar_members, not users), so it's saved separately
      // from the preferences PUT below. Only fires when it's actually
      // changed and the field is even visible (isPrimaryMember) — a
      // no-op call here would be harmless (the PATCH itself no-ops on an
      // unchanged value) but there's no reason to make it. Failure stops
      // the whole save here, same as every other step above, so a
      // rejected value (e.g. the 409 collision case) doesn't silently
      // get skipped while the rest of the save appears to succeed.
      // (21 Oct 2026, decision 120) The box's value is read from the screen (see
      // secondaryContactInputRef), and a saved change is named in the message below.
      const secondaryTyped = String(
        (secondaryContactInputRef.current ? secondaryContactInputRef.current.value : secondaryContactEmail) || ''
      ).trim();
      let secondaryNote = '';
      if (memberRole === 'primary' && secondaryTyped.toLowerCase() !== secondaryContactBaseline.trim().toLowerCase()) {
        try {
          const scRes = await fetch(`${apiBaseUrl}/api/calendars/${currentCalendar.id}/me/secondary-contact`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ secondaryContactEmail: secondaryTyped || null })
          });
          const scData = await scRes.json().catch(() => ({}));
          if (!scRes.ok) {
            onStatusMessage(scData.error || 'Failed to save secondary contact email', 'error');
            return;
          }
          setSecondaryContactEmail(secondaryTyped);
          setSecondaryContactBaseline(secondaryTyped);
          // The new partner's First Name isn't known until Settings is next opened.
          setSecondaryFirstName(null);
          secondaryNote = secondaryTyped
            ? `${secondaryTyped.toLowerCase()} is now your secondary contact.`
            : 'You no longer have a secondary contact.';
          // (23 Oct 2026, 8.44, decision 125, point 3) Entries marked for the former
          // partner were removed by the server; say so, and drop them from the list here.
          const removed = scData.removedDietary;
          if (removed && removed.count > 0) {
            secondaryNote += ` ${removed.name}'s ${removed.count} dietary ${removed.count === 1 ? 'entry was' : 'entries were'} removed.`;
            const removedIds = new Set(removed.ids || []);
            setDietaryFlags((prev) => prev.filter((f) => !removedIds.has(f.id)));
          }
        } catch (err) {
          onStatusMessage('Failed to save secondary contact email', 'error');
          return;
        }
      }

      // (26 Sep 2026, H3) Email setting for this calendar, only if changed.
      if (emailNotif.isMember && emailNotif.value &&
          (emailNotif.value !== emailNotif.saved || emailNotif.eventUpdates !== emailNotif.savedEventUpdates)) {
        const enRes = await fetch(`${apiBaseUrl}/api/calendars/${emailNotifCalendarId}/me/email-notifications`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailNotifications: emailNotif.value, emailEventUpdates: !!emailNotif.eventUpdates }),
        });
        if (!enRes.ok) {
          const enData = await enRes.json().catch(() => ({}));
          onStatusMessage(enData.error || 'Failed to save your email setting', 'error');
          return;
        }
        setEmailNotif((s) => ({ ...s, saved: s.value, savedEventUpdates: s.eventUpdates }));
      }

      // (15 Sep 2026) Normalize here too, not just on blur — a value can
      // reach this point without ever blurring (Enter-key submit,
      // autofill, a programmatic change) and this must match exactly
      // what userRoutes.js's own normalizeNameForComparison will store,
      // or the two stay out of sync the same way the original bug did.
      const normalizedFirstName = normalizeWhitespace(firstName);
      const normalizedLastName = normalizeWhitespace(lastName);
      const normalizedPreferredName = normalizeWhitespace(preferredName);

      // (2 Oct 2026, S1) Hosting location, as planned above.
      if (!(await runHostingSave(hostingPlan))) return;

      const response = await fetch(`${apiBaseUrl}/api/users/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferred_currency: preferences.currency,
          preferred_date_format: preferences.dateFormat,
          show_weekday: preferences.showWeekday !== false, // (24 Oct 2026, 8.40a, decision 100)
          // (26 Sep 2026) Only an administrator's Preferences show these two,
          // but sending the loaded values back unchanged is harmless.
          message_seconds: Number(preferences.messageSeconds) || 4,
          error_message_seconds: Number(preferences.errorMessageSeconds) || 8,
          preferred_timezone: preferences.timezone,
          preferred_language: preferences.language,
          first_name: normalizedFirstName,
          last_name: normalizedLastName,
          preferred_name: normalizedPreferredName || null,
          phone_number: phoneNumber.trim() || null,
          default_party_size: Number(defaultPartySize) || 1
        })
      });
      
      if (response.ok) {
        // (26 Sep 2026) Tell App.js the new message times, so they apply from
        // this very message on, without a reload.
        savedMessageTimesRef.current = {
          messageSeconds: Number(preferences.messageSeconds) || 4,
          errorMessageSeconds: Number(preferences.errorMessageSeconds) || 8,
        };
        window.dispatchEvent(new CustomEvent('calendarhub:message-times', {
          detail: { normal: Number(preferences.messageSeconds) || 4, error: Number(preferences.errorMessageSeconds) || 8 },
        }));
        // (24 Oct 2026, 8.40a, decision 100) Event dates change at once, without a reload
        // (App.js used to learn a new Date Format only at the next sign-in or reload).
        window.dispatchEvent(new CustomEvent('calendarhub:date-prefs', {
          detail: { dateFormat: preferences.dateFormat, showWeekday: preferences.showWeekday !== false },
        }));
        // (2 Oct 2026, S1) A hosting location that could not be saved keeps
        // its edits on screen (and Save Changes active) and says why.
        // (21 Oct 2026, decision 120) A saved secondary contact is named.
        const savedNotes = [secondaryNote, hostingPlan.note].filter(Boolean).join(' ');
        if (hostingPlan.action === 'skip') {
          onStatusMessage(`Settings saved. ${savedNotes}`, 'error');
          setHasChangesRaw(true);
          setChangedTabs(['hosting']);
        } else {
          onStatusMessage(savedNotes ? `Settings saved. ${savedNotes}` : 'Settings saved!', 'success');
          setHasChanges(false);
        }
        // (15 Sep 2026) Write the normalized values back into state — this
        // is the actual fix for the reported bug: previously the request
        // sent a corrected value but the input's own state kept whatever
        // was typed, so the field visibly disagreed with what got stored
        // the moment the modal was reopened (or even without reopening).
        setFirstName(normalizedFirstName);
        setLastName(normalizedLastName);
        setPreferredName(normalizedPreferredName);
        if (onSaveProfile) onSaveProfile(normalizedFirstName, normalizedLastName, normalizedPreferredName);
        // (22 Sep 2026) Onboarding exit point: the required-fields check
        // above already guarantees this save met the completion bar, so
        // once the PUT itself succeeds, tell the parent to move on
        // (App.js reloads, which re-runs landing-context and finds
        // needsProfileCompletion now false).
        if (requireProfileCompletion && onProfileCompleted) onProfileCompleted();
        // (8 Sep 2026) Deliberately NOT calling onClose() here anymore —
        // previously every successful save closed the whole modal and
        // reset the tab back to Profile (onClose does both), regardless
        // of which tab the person was actually on. Per the decision
        // agreed that day: Save Changes should save in place and let the
        // person keep working — closing is now only ever a deliberate
        // action (the X button or Cancel/Close), never an automatic side
        // effect of saving.
      } else {
        const data = await response.json().catch(() => ({}));
        onStatusMessage(data.error || 'Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      onStatusMessage('Failed to save settings', 'error');
    }
  };

  if (!isOpen) return null;

  // (21 Sep 2026) The sections this user can open, for the drop-down that replaces the tab list on a
  // phone. Keep it in step with the TabButtons below (same conditions, same order).
  const availableTabs = [
    { id: 'profile', label: 'Profile' },
    // (3 Oct 2026) Moved off Profile into tabs of their own. Allergies &
    // Aversions is locked during first-sign-in onboarding (7 Oct 2026, decision 25);
    // Hosting Location is not offered then, as before.
    { id: 'dietary', label: 'Allergies, Diets & Aversions' }, // (29 Oct 2026, decision 156)
    // (8 Oct 2026, decision 29) Not offered to someone who is only ever a secondary contact:
    // a couple's events use the primary's hosting location, so theirs would never be shown.
    ...(!requireProfileCompletion && !hideHostingTab ? [{ id: 'hosting', label: 'Hosting Location' }] : []),
    { id: 'preferences', label: 'Preferences' },
    ...(!restrictToProfileTabs && !restrictToDeputyTabs
      ? [
          { id: 'limits', label: 'Limits' },
          { id: 'calendar', label: 'Calendar Authority' },
          { id: 'members', label: 'Members' },
          { id: 'pollSettings', label: 'Poll Settings' },
          { id: 'hostBoard', label: 'Calendar Options' }, // (9 Oct 2026, decision 52)
          ...(raRegions.length > 0 ? [{ id: 'regionalAuthority', label: 'Regional Authority' }] : []),
          ...(isSA ? [{ id: 'systemAuthority', label: 'System Authority' }] : []),
        ]
      : []),
    ...(restrictToDeputyTabs && !restrictToProfileTabs
      ? [
          { id: 'pollSettings', label: 'Poll Settings' },
          { id: 'hostBoard', label: 'Calendar Options' }, // (9 Oct 2026, decision 52)
        ]
      : []),
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      {pinDialog && (
        <PinMismatchDialog
          dialog={pinDialog}
          onChoose={(choice) => { const r = pinDialog.resolve; setPinDialog(null); r(choice); }}
        />
      )}
      <div
        className="bg-white rounded-lg max-w-xl w-full mx-0 sm:mx-4 max-h-[90vh] min-h-[70vh] overflow-hidden flex flex-col"
        style={{ maxHeight: '90dvh', minHeight: '70dvh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
          {/* (22 Sep 2026) No way out until Profile is complete — same
              principle as hiding Close/Cancel in the footer below. */}
          {!requireProfileCompletion && (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Body: vertical tabs + content side by side */}
        <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">
          {/* Phone: one drop-down at the top (the content keeps the full width). */}
          <div className="sm:hidden flex-shrink-0 border-b p-3">
            <label htmlFor="settings-section" className="sr-only">Settings section</label>
            <select
              id="settings-section"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg bg-white text-base font-semibold text-gray-800 focus:outline-none focus:border-blue-500"
            >
              {availableTabs.map((t) => (
                <option key={t.id} value={t.id} disabled={requireProfileCompletion && t.id !== 'profile'}>
                  {t.label}{changedTabs.includes(t.id) ? ' •' : ''}{requireProfileCompletion && t.id !== 'profile' ? ' 🔒' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Tablet and desktop: the vertical tab list. */}
          <div className="hidden sm:flex w-40 flex-shrink-0 border-r p-3 flex-col gap-1 overflow-y-auto">
            <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
              <User className="w-4 h-4 flex-shrink-0" />
              Profile
              <UnsavedDot show={changedTabs.includes('profile')} />
            </TabButton>
            {/* (7 Oct 2026, 8.27, decision 25) Locked during first sign-in, like the other
                tabs, until the names are saved. */}
            <TabButton active={activeTab === 'dietary'} disabled={requireProfileCompletion} onClick={() => setActiveTab('dietary')}>
              <Utensils className="w-4 h-4 flex-shrink-0" />
              Allergies, Diets &amp; Aversions
              <UnsavedDot show={changedTabs.includes('dietary')} />
            </TabButton>
            {!requireProfileCompletion && !hideHostingTab && (
              <TabButton active={activeTab === 'hosting'} onClick={() => setActiveTab('hosting')}>
                <MapPin className="w-4 h-4 flex-shrink-0" />
                Hosting Location
                <UnsavedDot show={changedTabs.includes('hosting')} />
              </TabButton>
            )}
            <TabButton active={activeTab === 'preferences'} disabled={requireProfileCompletion} onClick={() => setActiveTab('preferences')}>
              <Globe className="w-4 h-4 flex-shrink-0" />
              Preferences
              <UnsavedDot show={changedTabs.includes('preferences')} />
            </TabButton>
            {!restrictToProfileTabs && !restrictToDeputyTabs && (
              <>
                <TabButton active={activeTab === 'limits'} disabled={requireProfileCompletion} onClick={() => setActiveTab('limits')}>
                  <Gauge className="w-4 h-4 flex-shrink-0" />
                  Limits
                </TabButton>
                <TabButton active={activeTab === 'calendar'} disabled={requireProfileCompletion} onClick={() => setActiveTab('calendar')}>
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  Calendar Authority
                </TabButton>
                <TabButton active={activeTab === 'members'} disabled={requireProfileCompletion} onClick={() => setActiveTab('members')}>
                  <UserPlus className="w-4 h-4 flex-shrink-0" />
                  Members
                </TabButton>
                <TabButton active={activeTab === 'pollSettings'} disabled={requireProfileCompletion} onClick={() => setActiveTab('pollSettings')}>
                  <ListChecks className="w-4 h-4 flex-shrink-0" />
                  Poll Settings
                </TabButton>
                {/* (21 Sep 2026) Always shown: the host board's on/off switch lives in this tab, so
                    it must stay reachable while the board is off. */}
                <TabButton active={activeTab === 'hostBoard'} disabled={requireProfileCompletion} onClick={() => setActiveTab('hostBoard')}>
                  <CalendarDays className="w-4 h-4 flex-shrink-0" />
                  Calendar Options
                  <UnsavedDot show={calendarOptionsHasChanges} />
                </TabButton>
                {raRegions.length > 0 && (
                  <TabButton active={activeTab === 'regionalAuthority'} disabled={requireProfileCompletion} onClick={() => setActiveTab('regionalAuthority')}>
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    Regional Authority
                  </TabButton>
                )}
                {isSA && (
                  <TabButton active={activeTab === 'systemAuthority'} disabled={requireProfileCompletion} onClick={() => setActiveTab('systemAuthority')}>
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    System Authority
                  </TabButton>
                )}
              </>
            )}
            {/* (11 Sep 2026) Active deputy (stand-in currently holding authority) —
                per the 10 Sep Poll Settings gating fix (isOwner -> isActiveHolder
                on the PATCH route), a deputy CAN save Poll Settings while holding
                authority, but should NOT reach Calendar Authority (reclaim/stand-in
                management is explicitly owner-only, per deputyRoutes.js), Members,
                Subscription, or Regional/System Authority. */}
            {restrictToDeputyTabs && !restrictToProfileTabs && (
              <TabButton active={activeTab === 'pollSettings'} disabled={requireProfileCompletion} onClick={() => setActiveTab('pollSettings')}>
                <ListChecks className="w-4 h-4 flex-shrink-0" />
                Poll Settings
              </TabButton>
            )}
            {restrictToDeputyTabs && !restrictToProfileTabs && (
              <TabButton active={activeTab === 'hostBoard'} disabled={requireProfileCompletion} onClick={() => setActiveTab('hostBoard')}>
                <CalendarDays className="w-4 h-4 flex-shrink-0" />
                Calendar Options
                <UnsavedDot show={calendarOptionsHasChanges} />
              </TabButton>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Always-visible scrollbar (iPhone otherwise hides it until you swipe). */}
        <style>{`
          .settings-scroll { scrollbar-width: thin; scrollbar-color: #9ca3af #f3f4f6; }
          .settings-scroll::-webkit-scrollbar { -webkit-appearance: none; width: 8px; }
          .settings-scroll::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 4px; }
          .settings-scroll::-webkit-scrollbar-thumb { background: #9ca3af; border-radius: 4px; }
        `}</style>
        <div
          ref={scrollRef}
          onScroll={updateScrollHint}
          className="settings-scroll p-4 sm:p-6 overflow-y-auto flex-1 overscroll-contain"
        >
          <div ref={contentRef}>
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              {activeTab === 'profile' && (
                <div>
                  {/* (22 Sep 2026) Onboarding banner — only ever shown while
                      requireProfileCompletion is set (first sign-in with a
                      blank profile); a returning user editing Profile
                      normally never sees this. */}
                  {requireProfileCompletion && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-gray-800">Please complete your profile to continue</p>
                      <p className="text-sm text-gray-700 mt-1">
                        {/* (8 Oct 2026, decision 27) A secondary contact reads "First name and last name are required…". */}
                        {showAsSecondary ? 'First name and last name' : 'First name, last name and preferred name'} are required before you can use Calendar Hub.
                      </p>
                    </div>
                  )}
                  {/* (12 Sep 2026) Authority is otherwise invisible anywhere
                      in the app — a user with GA/RA/SA roles across
                      several calendars/regions had no single place to see
                      everything they hold, short of clicking into each
                      Calendar/Regional/System Authority tab individually
                      (which a plain member wouldn't even think to try).
                      Reuses isSA/raRegions/authorityCalendars, already
                      computed once by landing-context on login — no new
                      fetch. isActiveHolder distinguishes genuinely holding
                      a role right now from merely being eligible (e.g. a
                      GA who's handed off to a stand-in). */}
                  {/* (27 Oct 2026, decision 148) Titles follow the Settings tab names:
                      System Authority, Regional Authority, Group Administrator.
                      Decision 149 adds the region-scoped Group Administrator line. */}
                  {(isSA || raRegions.length > 0 || gaRegions.length > 0 || authorityCalendars.length > 0) && (
                    <div className="mb-4 bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <p className="text-sm font-semibold text-gray-800 mb-2">Your roles</p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        {isSA && (
                          <li>System Authority</li>
                        )}
                        {raRegions.map((r) => (
                          <li key={r.id}>
                            Regional Authority — {r.name}
                            {!r.isActiveHolder && <span className="text-gray-500"> (handed off to {r.activeHolderName || 'a stand-in'})</span>}
                          </li>
                        ))}
                        {gaRegions.map((g) => (
                          <li key={`ga-${g.regionId}`}>
                            Group Administrator — {g.regionName || 'region'}
                            {g.cap != null && ` (up to ${g.cap} calendar${g.cap === 1 ? '' : 's'})`}
                          </li>
                        ))}
                        {authorityCalendars.map((c) => (
                          <li key={c.id}>
                            Group Administrator — {c.name}
                            {!c.isActiveHolder && <span className="text-gray-500"> (handed off to a stand-in)</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <h3 className="text-lg font-semibold mb-4">Profile Information</h3>
                  <div className="space-y-4">
                    {/* (21 Sep 2026) Moved up from the bottom of this section: it is the first thing a
                        user should see, and it is no longer shown in the page header. */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <p className="text-gray-900 text-sm break-all">{userEmail}</p>
                      <p className="text-xs text-gray-500 mt-1">This is the address you sign in with.</p>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="fld-a1"
                          autoComplete="off"
                          required
                          value={firstName}
                          onChange={(e) => { setFirstName(e.target.value); markTouched('firstName'); setHasChanges(true); }}
                          onBlur={() => setFirstName((v) => normalizeWhitespace(v))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="fld-a2"
                          autoComplete="off"
                          required
                          value={lastName}
                          onChange={(e) => { setLastName(e.target.value); markTouched('lastName'); setHasChanges(true); }}
                          onBlur={() => setLastName((v) => normalizeWhitespace(v))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                    {showAsSecondary ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Name</label>
                        {memberRole === 'secondary' && (
                          <p className="text-gray-900 text-sm">{primaryName || '(not set)'}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          You're shown under your partner's preferred name for this calendar — there's nothing to set here yourself.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          required
                          value={preferredName}
                          onChange={(e) => { setPreferredName(e.target.value); markTouched('preferredName'); setHasChanges(true); }}
                          onBlur={() => setPreferredName((v) => normalizeWhitespace(v))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="What the group calls you"
                        />
                        <p className="text-xs text-gray-500 mt-1">Your individual or couple names, e.g. Jim or Neil &amp; Michelle.</p>
                      </div>
                    )}
                    {memberRole === 'primary' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Contact Email (optional)</label>
                        <input
                          type="email"
                          name="fld-a4"
                          autoComplete="off"
                          ref={secondaryContactInputRef}
                          value={secondaryContactEmail}
                          onChange={(e) => { setSecondaryContactEmail(e.target.value); markTouched('secondaryContactEmail'); setHasChanges(true); }}
                          onBlur={(e) => {
                            // (21 Oct 2026, decision 120) Catches a value filled in without a
                            // change event (Safari AutoFill), so the footer reads "Save Changes".
                            if (e.target.value !== secondaryContactEmail) {
                              setSecondaryContactEmail(e.target.value); markTouched('secondaryContactEmail'); setHasChanges(true);
                            }
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="partner@example.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Let a partner sign in and answer polls for this calendar using their own email — you'll share one membership, one response. Leave blank if not applicable.
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (optional)</label>
                      <input
                        type="tel"
                        name="fld-a3"
                        autoComplete="off"
                        value={phoneNumber}
                        onChange={(e) => { setPhoneNumber(e.target.value); markTouched('phoneNumber'); setHasChanges(true); }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. +33 6 12 34 56 78"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Shared with dish contributors alongside your dietary flags, so they can reach you directly if needed.
                        Include the country code (e.g. +33 for France, +44 for UK)
                      </p>
                    </div>
                    {showAsSecondary ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Default Party Size</label>
                        {memberRole === 'secondary' && (
                          <p className="text-gray-900 text-sm">{primaryDefaultPartySize}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Attendance for this calendar uses your partner's party size setting — there's nothing to set here yourself.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Default Party Size</label>
                        <input
                          type="number"
                          min="1"
                          value={defaultPartySize}
                          onChange={(e) => { setDefaultPartySize(e.target.value); setHasChanges(true); }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Pre-fills party size when you confirm attendance.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* (3 Oct 2026) Allergies & Aversions and Hosting Location, moved
                  off the foot of Profile so phone users see they exist. Still
                  saved by the one Save Changes button with everything else. */}
              {activeTab === 'dietary' && (
                <DietaryFlagsSection
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  onStatusMessage={onStatusMessage}
                  setHasChanges={setHasChanges}
                  flags={dietaryFlags}
                  setFlags={setDietaryFlags}
                  pendingSeverityChanges={pendingSeverityChanges}
                  setPendingSeverityChanges={setPendingSeverityChanges}
                  pendingAdds={pendingAdds}
                  setPendingAdds={setPendingAdds}
                  pendingRemoveIds={pendingRemoveIds}
                  setPendingRemoveIds={setPendingRemoveIds}
                  appliesToChoices={memberRole === 'primary' && secondaryContactBaseline.trim()
                    ? [
                      { value: 'both', label: 'Both' },
                      { value: 'primary', label: normalizeWhitespace(firstName) || 'Me' },
                      { value: 'secondary', label: secondaryFirstName || secondaryContactBaseline.trim() },
                    ]
                    : null}
                  pendingAppliesToChanges={pendingAppliesToChanges}
                  setPendingAppliesToChanges={setPendingAppliesToChanges}
                />
              )}

              {/* (30 Sep 2026, H17) Not during first-sign-in onboarding. */}
              {activeTab === 'hosting' && !requireProfileCompletion && !hideHostingTab && (
                <HostingLocationSection
                  hosting={hosting}
                  onChange={changeHosting}
                  onUseCurrentLocation={useCurrentHostingLocation}
                  onFindAddress={findHostingAddress}
                  onRemove={removeHosting}
                  locating={hostingLocating}
                  finding={hostingFinding}
                  removing={hostingRemoving}
                />
              )}
              
              {activeTab === 'preferences' && (
                <>
                  <PreferencesTab 
                    preferences={preferences} 
                    setPreferences={setPreferences}
                    setHasChanges={setHasChanges}
                    showMessageTimes={!restrictToProfileTabs}
                    // (30 Sep 2026, H16) Members: a read-only line about their
                    // device's time zone instead of the (unused) setting.
                    memberView={!!restrictToProfileTabs}
                    calendarTimezone={currentCalendar?.timezone || emailNotif.calendarTimezone || null}
                    calendarName={currentCalendar?.name}
                  />
                  {emailNotif.isMember && emailNotif.value && (
                    <EmailNotificationsSetting
                      calendarName={currentCalendar?.name}
                      value={emailNotif.value}
                      onChange={(v) => { setEmailNotif((s) => ({ ...s, value: v })); setHasChanges(true); }}
                      eventUpdates={emailNotif.eventUpdates}
                      administratorFirstName={emailNotif.administratorFirstName}
                      isAdministrator={emailNotif.isAdministrator}
                      onEventUpdatesChange={(on) => { setEmailNotif((s) => ({ ...s, eventUpdates: on })); setHasChanges(true); }}
                    />
                  )}
                </>
              )}
              
              {activeTab === 'limits' && (
                <LimitsTab userTier={userTier} currentCalendar={currentCalendar} />
              )}

              {activeTab === 'calendar' && (
                <CalendarTab
                  currentCalendar={currentCalendar}
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  onStatusMessage={onStatusMessage}
                />
              )}

              {activeTab === 'members' && (
                <MembersTab
                  currentCalendar={currentCalendar}
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  onStatusMessage={onStatusMessage}
                  onAnswerPollForMember={onAnswerPollForMember}
                  userTier={userTier}
                />
              )}

              {activeTab === 'pollSettings' && (
                <PollSettingsTab
                  ref={pollSettingsRef}
                  currentCalendar={currentCalendar}
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  onStatusMessage={onStatusMessage}
                  // (31 Oct 2026, decision 163) For the poll date simulator's dates.
                  dateFormat={preferences.dateFormat}
                  showWeekday={preferences.showWeekday !== false}
                  onStateChange={({ hasChanges, isSaving }) => {
                    setPollSettingsHasChanges(hasChanges);
                    setPollSettingsIsSaving(isSaving);
                  }}
                />
              )}

              {/* (9 Oct 2026, decisions 52, 59, 60) "Calendar Options": the hosting schedule
                  and the calendar history, saved with the footer button. Kept
                  mounted while another tab is open, so unsaved changes survive switching
                  tabs (and its amber dot means something). */}
              {!restrictToProfileTabs && currentCalendar && (
                <div className={activeTab === 'hostBoard' ? '' : 'hidden'}>
                  <CalendarOptionsTab
                    ref={calendarOptionsRef}
                    currentCalendar={currentCalendar}
                    apiBaseUrl={apiBaseUrl}
                    authToken={authToken}
                    onStatusMessage={onStatusMessage}
                    onStateChange={({ hasChanges: changed, isSaving: saving }) => {
                      setCalendarOptionsHasChanges(changed);
                      setCalendarOptionsIsSaving(saving);
                    }}
                    onSaved={onCalendarOptionsSaved}
                  />
                </div>
              )}

              {activeTab === 'regionalAuthority' && (
                <RegionalAuthorityTab
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  raRegions={raRegions}
                  onStatusMessage={onStatusMessage}
                />
              )}

              {activeTab === 'systemAuthority' && (
                <SystemAuthorityTab
                  apiBaseUrl={apiBaseUrl}
                  authToken={authToken}
                  isSA={isSA}
                  onStatusMessage={onStatusMessage}
                />
              )}
            </>
          )}
          </div>{/* end measured content */}
        </div>
        {canScrollDown && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent flex items-end justify-center pb-2">
            <button
              type="button"
              onClick={() => {
                const el = scrollRef.current;
                if (el) el.scrollBy({ top: Math.max(120, el.clientHeight * 0.8), behavior: 'smooth' });
              }}
              aria-label="Scroll down for more"
              className="pointer-events-auto rounded-full bg-white border border-gray-300 shadow p-1.5 text-gray-600 hover:text-gray-900"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
        )}

          </div>{/* end content */}
        </div>{/* end body */}

        {/* Footer — hide Save button on tabs with no batched changes to
            save. (15 Sep 2026) pollSettings moved OUT of that no-save
            group — it now gets its own Save Poll Settings button here,
            wired to PollSettingsTab via pollSettingsRef/onStateChange
            rather than the shared handleSave/hasChanges used by
            Profile/Preferences. */}
        <div className="flex justify-end gap-3 p-5 border-t">
          {/* (22 Sep 2026) No Cancel/Close while Profile completion is
              required — activeTab can only be 'profile' here anyway
              (every other TabButton is disabled above), so this button
              would always read 'Cancel', but hiding it outright is more
              honest about there being no way to leave. */}
          {!requireProfileCompletion && (
            <button 
              onClick={onClose} 
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {['limits', 'calendar', 'members', 'regionalAuthority', 'systemAuthority'].includes(activeTab) ? 'Close' : 'Cancel'}
            </button>
          )}
          {activeTab === 'hostBoard' ? (
            <button
              // (9 Oct 2026, decision 60) Like Save Poll Settings: "Saved ✓" when nothing is unsaved.
              onClick={() => { if (calendarOptionsHasChanges) calendarOptionsRef.current?.save(); }}
              disabled={calendarOptionsIsSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {calendarOptionsIsSaving && <RefreshCw className="w-4 h-4 animate-spin" />}
              {calendarOptionsIsSaving ? 'Saving…' : (calendarOptionsHasChanges ? 'Save Calendar Options' : 'Saved ✓')}
            </button>
          ) : activeTab === 'pollSettings' ? (
            <button
              // (8 Oct 2026, 8.14) Never disabled for "nothing to save": like Save Changes,
              // it reads "Saved ✓" when there is nothing unsaved, and pressing it then does nothing.
              onClick={() => { if (pollSettingsHasChanges) pollSettingsRef.current?.save(); }}
              disabled={pollSettingsIsSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pollSettingsIsSaving && <RefreshCw className="w-4 h-4 animate-spin" />}
              {pollSettingsIsSaving ? 'Saving…' : (pollSettingsHasChanges ? 'Save Poll Settings' : 'Saved ✓')}
            </button>
          ) : (
            !['limits', 'calendar', 'members', 'regionalAuthority', 'systemAuthority'].includes(activeTab) && (
              // (3 Oct 2026) The label says whether anything is unsaved:
              // "Save Changes" while there are changes, "Saved ✓" when
              // there are none. While the pin/address question is open
              // nothing is being saved yet, so it stays "Save Changes"
              // (not "Saving…"); "Saving…" only once the save has started.
              <button 
                onClick={handleSave}
                disabled={(isSaving && !pinDialog) || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving && !pinDialog && <RefreshCw className="w-4 h-4 animate-spin" />}
                {isSaving && !pinDialog
                  ? 'Saving…'
                  : ((hasChanges || requireProfileCompletion || isSaving) ? 'Save Changes' : 'Saved ✓')}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
